import { MapContainer, TileLayer, Circle, CircleMarker, Marker, Popup, Tooltip, ZoomControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { ZONES } from "@/lib/zones";

// Fix default marker icons (Leaflet expects assets on same-origin, not our webpack bundle)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const POLE_COORDS = {
  chartres:   { lat: 48.4470, lng: 1.4890 },
  chateaudun: { lat: 48.0733, lng: 1.3288 },
  dreux:      { lat: 48.7375, lng: 1.3661 },
  brou:       { lat: 48.2159, lng: 1.1636 },
  epernon:    { lat: 48.6089, lng: 1.6790 },
  nogent:     { lat: 48.3244, lng: 0.8206 },
  bonneval:   { lat: 48.1786, lng: 1.3897 },
  courville:  { lat: 48.4500, lng: 1.2403 },
  illiers:    { lat: 48.3006, lng: 1.2475 },
};

const DEPT_CENTER = [48.42, 1.35];
const DEFAULT_ZOOM = 9;
const RADIUS_METERS = 15000;

function poleIcon(count) {
  const html = `
    <div style="position:relative;transform:translate(-50%,-100%)">
      <div style="width:34px;height:34px;border-radius:50%;background:#E05638;box-shadow:0 4px 10px rgba(224,86,56,.45);
                  display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;
                  border:3px solid #fff;font-family:Outfit,sans-serif;font-size:13px">${count || ""}</div>
      <div style="position:absolute;left:50%;top:34px;transform:translateX(-50%);
                  width:0;height:0;border:6px solid transparent;border-top-color:#E05638"></div>
    </div>`;
  return L.divIcon({ html, className: "", iconSize: [34, 44], iconAnchor: [17, 44] });
}

export default function DepartmentMap({ counts = {}, businesses = [] }) {
  const navigate = useNavigate();

  const bizPoints = useMemo(() => (
    (businesses || []).filter((b) => typeof b.lat === "number" && typeof b.lng === "number")
  ), [businesses]);

  return (
    <div className="w-full">
      <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white"
           data-testid="leaflet-department-map">
        <MapContainer
          center={DEPT_CENTER}
          zoom={DEFAULT_ZOOM}
          scrollWheelZoom={false}
          zoomControl={false}
          style={{ height: "560px", width: "100%" }}
        >
          <ZoomControl position="bottomright" />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {ZONES.map((z) => {
            const c = POLE_COORDS[z.id];
            if (!c) return null;
            const count = counts[z.id] || 0;
            return (
              <div key={z.id}>
                <Circle
                  center={c}
                  radius={RADIUS_METERS}
                  pathOptions={{ color: "#E05638", weight: 1.5, fillColor: "#E05638", fillOpacity: 0.10, dashArray: "6 6" }}
                  eventHandlers={{ click: () => navigate(`/pole/${z.id}`) }}
                />
                <Marker
                  position={c}
                  icon={poleIcon(count)}
                  eventHandlers={{ click: () => navigate(`/pole/${z.id}`) }}
                >
                  <Tooltip direction="top" offset={[0, -40]} opacity={1} permanent={false}>
                    <div style={{ fontFamily: "Outfit, sans-serif" }}>
                      <div style={{ fontWeight: 700 }}>{z.center}</div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>{count} offre{count>1?"s":""} · rayon 15 km</div>
                    </div>
                  </Tooltip>
                </Marker>
              </div>
            );
          })}

          {bizPoints.map((b) => (
            <CircleMarker
              key={b.id}
              center={[b.lat, b.lng]}
              radius={6}
              pathOptions={{ color: "#059669", weight: 2, fillColor: "#10B981", fillOpacity: 0.9 }}
            >
              <Popup>
                <div style={{ fontFamily: "Outfit, sans-serif", minWidth: 160 }}>
                  <div style={{ fontWeight: 700, marginBottom: 2 }}>{b.name}</div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>{b.city}</div>
                  <a href={`/commerce/${b.id}`} style={{ display: "inline-block", marginTop: 6, fontSize: 12, color: "#E05638", fontWeight: 600 }}>
                    Voir le commerce →
                  </a>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>

        <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-3 py-2 rounded-xl border border-slate-200 shadow-sm text-xs text-slate-600 z-[500]"
             data-testid="map-legend">
          <div className="font-bold text-slate-900 mb-1">Eure-et-Loir · 9 pôles</div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-[#E05638]"></span> Pôles (15&nbsp;km)
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="inline-block w-3 h-3 rounded-full bg-emerald-500"></span> Commerces partenaires
          </div>
        </div>
      </div>
    </div>
  );
}
